<template>
  <el-dialog title="AI 创建脚手架" :visible.sync="visible" width="850px" top="5vh" @close="handleClose" :close-on-click-modal="false">
    <!-- 步骤条 -->
    <el-steps :active="currentStep" finish-status="success" align-center style="margin-bottom:24px;">
      <el-step title="选择项目"></el-step>
      <el-step title="项目分析"></el-step>
      <el-step title="AI 分析"></el-step>
      <el-step title="审核提交"></el-step>
    </el-steps>

    <!-- Step 0: 选择本地项目 + 填写基本信息 -->
    <div v-if="currentStep === 0" class="step-content">
      <el-form label-width="120px" size="small">
        <el-form-item label="本地项目" required>
          <div style="display:flex;gap:8px;align-items:center;">
            <el-input :value="selectedPath" placeholder="请选择本地项目目录" disabled style="flex:1;" />
            <el-button type="primary" @click="selectDirectory" icon="el-icon-folder-opened">选择目录</el-button>
          </div>
          <p v-if="analyzedFileCount > 0" style="color:#909399;font-size:12px;margin:4px 0 0;">
            已扫描 {{ analyzedFileCount }} 个文件
            <el-tag v-if="detectedProjectType" size="mini" style="margin-left:8px;">
              {{ detectedProjectType.label }}
            </el-tag>
            <el-tag v-if="detectedFrameworks.length > 0" size="mini" type="warning" style="margin-left:4px;">
              {{ detectedFrameworks[0] }}
            </el-tag>
          </p>
        </el-form-item>
        <el-form-item label="脚手架名称" required>
          <el-input v-model="scaffoldName" placeholder="请输入脚手架名称" clearable />
        </el-form-item>
        <el-form-item label="脚手架编码" required>
          <el-input v-model="scaffoldCode" placeholder="请输入脚手架编码，如 my-scaffold" clearable />
        </el-form-item>
        <el-form-item label="版本号" required>
          <el-input v-model="scaffoldVersion" placeholder="如 1.0.0" clearable />
        </el-form-item>
        <el-form-item label="脚手架描述">
          <el-input v-model="scaffoldDescription" type="textarea" :rows="2" placeholder="简短描述此脚手架的用途" clearable />
        </el-form-item>
      </el-form>
    </div>

    <!-- Step 1: 项目分析（扫描目录 + 检测类型） -->
    <div v-if="currentStep === 1" class="step-content" style="text-align:center;padding:30px 0;">
      <div v-if="!scanDone">
        <i class="el-icon-loading" style="font-size:48px;color:#409EFF;"></i>
        <p style="margin-top:16px;color:#606266;">正在扫描项目文件，请稍候...</p>
        <p style="color:#909399;font-size:13px;">已扫描 {{ analyzedFileCount }} 个文件</p>
      </div>
      <div v-else style="text-align:left;max-width:500px;margin:0 auto;">
        <div style="text-align:center;margin-bottom:20px;">
          <i class="el-icon-success" style="font-size:48px;color:#67C23A;"></i>
          <p style="margin-top:12px;color:#606266;font-size:15px;">项目分析完成</p>
        </div>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="项目类型">
            <el-tag size="mini">{{ detectedProjectType ? detectedProjectType.label : '未知' }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="文件总数">{{ analyzedFileCount }}</el-descriptions-item>
          <el-descriptions-item label="检测到框架">
            <span v-if="detectedFrameworks.length > 0">
              <el-tag v-for="fw in detectedFrameworks" :key="fw" size="mini" type="warning" style="margin-right:4px;">{{ fw }}</el-tag>
            </span>
            <span v-else style="color:#909399;">未检测到</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </div>

    <!-- Step 2: AI 分析中（递归探索） -->
    <div v-if="currentStep === 2" class="step-content" style="text-align:center;padding:30px 0;">
      <div v-if="!aiDone">
        <i class="el-icon-loading" style="font-size:48px;color:#409EFF;"></i>
        <p style="margin-top:16px;color:#606266;font-weight:500;">AI 正在递归探索项目...</p>
        <el-progress :percentage="aiProgress" :stroke-width="6" style="width:300px;margin:20px auto 0;" />
        <p style="color:#409EFF;font-size:13px;margin-top:12px;">
          <i class="el-icon-document-copy"></i> {{ currentAiAction || '准备中...' }}
        </p>
        <!-- AI 探索日志 -->
        <div v-if="aiLogs.length > 0" style="margin-top:16px;text-align:left;max-width:500px;margin-left:auto;margin-right:auto;background:#f5f7fa;border-radius:4px;padding:12px;max-height:200px;overflow-y:auto;">
          <div v-for="(log, i) in aiLogs" :key="i" style="font-size:12px;color:#606266;line-height:1.8;padding:2px 0;border-bottom:1px solid #ebeef5;">
            {{ log }}
          </div>
        </div>
      </div>
      <div v-else>
        <i class="el-icon-success" style="font-size:48px;color:#67C23A;"></i>
        <p style="margin-top:16px;color:#606266;">分析完成！AI 已成功提取脚手架配置信息。</p>
        <p style="color:#909399;font-size:13px;margin-top:4px;">
          识别到 {{ aiResult.modules ? aiResult.modules.length : 0 }} 个模块、
          {{ aiResult.configSchema ? aiResult.configSchema.length : 0 }} 个配置组、
          {{ aiResult.envRequirements ? aiResult.envRequirements.length : 0 }} 个环境要求
        </p>
      </div>
    </div>

    <!-- Step 3: 审核提交 -->
    <div v-if="currentStep === 3" class="step-content">
      <el-tabs v-model="reviewTab" type="border-card">
        <el-tab-pane label="基本信息" name="info">
          <el-form label-width="120px" size="small">
            <el-form-item label="脚手架描述">
              <el-input v-model="scaffoldDescription" type="textarea" :rows="3" clearable />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="环境要求" name="env">
          <el-input v-model="envRequirementsText" type="textarea" :rows="6" clearable
            placeholder='[{"name":"JDK","version":"17","type":"runtime"}]' />
          <p style="color:#909399;font-size:12px;margin-top:4px;">JSON 数组，每个元素有 name/version/type（runtime|build|tool）</p>
        </el-tab-pane>
        <el-tab-pane label="配置清单" name="config">
          <el-input v-model="configSchemaText" type="textarea" :rows="12" clearable
            placeholder='[{"group":"数据库","fields":[...]}]' />
          <p style="color:#909399;font-size:12px;margin-top:4px;">JSON 数组，按 group 分组，每个 field 有 key/label/type/required/default/description/order</p>
        </el-tab-pane>
        <el-tab-pane label="脚手架压缩包" name="upload">
          <file-link-upload-select v-model="scaffoldFilePath" object-key-prefix="scaffold" placeholder="请上传脚手架压缩包" :max-size="100 * 1024 * 1024" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <span slot="footer">
      <el-button @click="handleClose" size="small">取消</el-button>
      <el-button v-if="currentStep > 0 && !(currentStep === 2 && !aiDone)" @click="prevStep" size="small">上一步</el-button>

      <!-- Step 0: 开始分析 -->
      <el-button v-if="currentStep === 0" type="primary" @click="startProjectScan"
        :disabled="!directoryHandle || !scaffoldName || !scaffoldCode || !scaffoldVersion"
        :loading="scanning" size="small">
        扫描项目 <i class="el-icon-arrow-right"></i>
      </el-button>

      <!-- Step 1: 开始 AI 分析 -->
      <el-button v-if="currentStep === 1 && scanDone" type="primary" @click="startAIAnalysis"
        :loading="aiRunning" size="small">
        开始 AI 分析 <i class="el-icon-arrow-right"></i>
      </el-button>

      <!-- Step 3: 创建 -->
      <el-button v-if="currentStep === 3" type="primary" @click="submitScaffold" :loading="submitting" size="small">
        创建脚手架
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import FileLinkUploadSelect from "@/components/select/FileLinkUploadSelect";
import { addData } from "@/api/baseApi";
import {
  walkDirectory,
  detectProjectType,
  detectFrameworks,
  buildStructureTree,
  normalizeAnalysisResult,
  runMultiTurnAnalysis,
} from "@/utils/aiProjectAnalyzer";

export default {
  name: "ScaffoldCreateWizard",
  components: { FileLinkUploadSelect },
  props: {
    visible: Boolean
  },
  data() {
    return {
      currentStep: 0,
      directoryHandle: null,
      selectedPath: '',
      analyzedFileCount: 0,
      projectFiles: [],
      structureLines: [],
      detectedProjectType: null,
      detectedFrameworks: [],
      scaffoldName: '',
      scaffoldCode: '',
      scaffoldVersion: '1.0.0',
      scaffoldDescription: '',
      configSchemaText: '',
      envRequirementsText: '',
      scaffoldFilePath: '',
      scanning: false,
      scanDone: false,
      aiRunning: false,
      aiDone: false,
      aiProgress: 0,
      aiResult: {},
      currentAiAction: '',
      aiLogs: [],
      submitting: false,
      reviewTab: 'info',
    }
  },
  watch: {
    visible(val) {
      if (val) this.resetForm();
    }
  },
  methods: {
    resetForm() {
      this.currentStep = 0;
      this.directoryHandle = null;
      this.selectedPath = '';
      this.analyzedFileCount = 0;
      this.projectFiles = [];
      this.structureLines = [];
      this.detectedProjectType = null;
      this.detectedFrameworks = [];
      this.scaffoldName = '';
      this.scaffoldCode = '';
      this.scaffoldVersion = '1.0.0';
      this.scaffoldDescription = '';
      this.configSchemaText = '';
      this.envRequirementsText = '';
      this.scaffoldFilePath = '';
      this.scanning = false;
      this.scanDone = false;
      this.aiRunning = false;
      this.aiDone = false;
      this.aiProgress = 0;
      this.aiResult = {};
      this.currentAiAction = '';
      this.aiLogs = [];
      this.reviewTab = 'info';
    },
    async selectDirectory() {
      try {
        if (!window.showDirectoryPicker) {
          this.$message.error('当前浏览器不支持 File System Access API，请使用 Chrome 86+');
          return;
        }
        this.directoryHandle = await window.showDirectoryPicker();
        this.selectedPath = this.directoryHandle.name;
        // 重置之前的扫描结果
        this.scanDone = false;
        this.projectFiles = [];
        this.structureLines = [];
        this.detectedProjectType = null;
        this.detectedFrameworks = [];
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('选择目录失败:', e);
          this.$message.error('选择目录失败');
        }
      }
    },
    async startProjectScan() {
      if (!this.directoryHandle) return;
      this.scanning = true;
      this.currentStep = 1;

      try {
        // 1. 遍历目录
        const { files, structureLines } = await walkDirectory(this.directoryHandle);
        this.projectFiles = files;
        this.structureLines = structureLines;
        this.analyzedFileCount = files.length;

        // 2. 检测项目类型
        this.detectedProjectType = detectProjectType(files).primary;

        // 3. 检测框架（基于文件名启发式检测）
        this.detectedFrameworks = this.detectFrameworksLight(files, this.detectedProjectType.key);

        this.scanDone = true;

        // 自动预填脚手架名称
        if (!this.scaffoldName) {
          this.scaffoldName = this.selectedPath || this.detectedProjectType.name + ' Scaffold';
        }
      } catch (e) {
        console.error('项目扫描失败:', e);
        this.$message.error('项目扫描失败: ' + (e.message || '未知错误'));
      } finally {
        this.scanning = false;
      }
    },
    async startAIAnalysis() {
      this.aiRunning = true;
      this.aiProgress = 0;
      this.currentStep = 2;
      this.aiLogs = [];

      try {
        const scanResult = {
          structureLines: this.structureLines,
          projectFiles: this.projectFiles,
          detectedProjectType: this.detectedProjectType,
          detectedFrameworks: this.detectedFrameworks,
          scaffoldName: this.scaffoldName || this.selectedPath,
        };

        const result = await runMultiTurnAnalysis(
          this.directoryHandle,
          scanResult,
          this.$ai,
          (progress) => {
            const { phase, message, filesToRead } = progress;
            this.currentAiAction = message || '分析中...';
            if (phase === 'thinking') {
              this.aiProgress = Math.min(this.aiProgress + 10, 80);
              this.aiLogs.push('🤔 ' + (message || 'AI 分析中...'));
            } else if (phase === 'reading') {
              this.aiProgress = Math.min(this.aiProgress + 5, 70);
              this.aiLogs.push('📖 ' + (message || 'AI 请求读取文件'));
            } else if (phase === 'done') {
              this.aiProgress = 100;
              this.aiLogs.push('✅ ' + (message || '分析完成'));
            } else if (phase === 'error') {
              this.aiLogs.push('⚠️ ' + (message || '分析异常'));
            }
            if (this.aiLogs.length > 20) {
              this.aiLogs = this.aiLogs.slice(-20);
            }
          }
        );

        this.aiResult = result;
        this.applyAiResult(result);
        this.aiProgress = 100;
        this.aiDone = true;
        this.currentStep = 3;
      } catch (e) {
        console.error('AI 分析失败:', e);
        this.$message.error('AI 分析失败: ' + (e.message || '未知错误'));
        this.aiDone = true;
        this.currentStep = 3;
      } finally {
        this.aiRunning = false;
      }
    },

    /**
     * 基于文件名启发式检测框架（无需读取文件内容）
     */
    detectFrameworksLight(files, projectTypeKey) {
      const frameworks = [];
      const fileNames = files.map(f => f.name);
      const filePaths = files.map(f => f.path);

      if (projectTypeKey === 'MAVEN' || projectTypeKey === 'GRADLE') {
        if (fileNames.some(n => n === 'pom.xml')) {
          const pomFiles = files.filter(f => f.name === 'pom.xml');
          // 根目录下有 pom.xml 且有 src 目录 → Spring Boot 项目
          if (pomFiles.some(f => f.path === 'pom.xml')) {
            frameworks.push('Spring Boot');
          }
        }
        if (fileNames.some(n => n === 'application.yml' || n === 'application.yaml' || n === 'application.properties')) {
          if (!frameworks.includes('Spring Boot')) frameworks.push('Spring Boot');
        }
      } else if (projectTypeKey === 'NODE') {
        if (fileNames.some(n => n === 'package.json')) {
          // 常见前端框架识别
        }
      }

      return frameworks;
    },
    applyAiResult(result) {
      if (result.projectDescription) {
        this.scaffoldDescription = result.projectDescription;
      }
      if (result.configSchema && result.configSchema.length > 0) {
        this.configSchemaText = JSON.stringify(result.configSchema, null, 2);
      }
      if (result.envRequirements && result.envRequirements.length > 0) {
        this.envRequirementsText = JSON.stringify(result.envRequirements, null, 2);
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    async submitScaffold() {
      if (!this.scaffoldFilePath) {
        this.$message.warning('请上传脚手架压缩包');
        return;
      }
      this.submitting = true;
      try {
        const param = {
          name: this.scaffoldName,
          code: this.scaffoldCode,
          version: this.scaffoldVersion,
          description: this.scaffoldDescription,
          scaffoldFilePath: this.scaffoldFilePath,
          configSchema: this.configSchemaText,
          envRequirements: this.envRequirementsText,
        };
        const res = await addData("data", "scaffold", param);
        if (res.code === 200) {
          this.$message.success('脚手架创建成功');
          this.$emit('created');
          this.$emit('update:visible', false);
        } else {
          this.$message.error(res.message || '创建失败');
        }
      } catch (e) {
        console.error('创建脚手架失败:', e);
        this.$message.error('创建失败: ' + (e.message || '未知错误'));
      } finally {
        this.submitting = false;
      }
    },
    handleClose() {
      this.$emit('update:visible', false);
    }
  }
}
</script>

<style scoped>
.step-content {
  min-height: 200px;
}
</style>
